import React, { useState, useEffect, useRef } from 'react';
import { downloadVCard } from '../../../utils/vcard';
import './styles.css';

interface VoiceContactFormProps {
  onComplete?: (name: string, phone: string) => void;
}

export const VoiceContactForm: React.FC<VoiceContactFormProps> = ({ 
  onComplete 
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSpeechSupported, setIsSpeechSupported] = useState<boolean | null>(null);
  const [currentField, setCurrentField] = useState<'name' | 'phone'>('name');
  const [showTooltip, setShowTooltip] = useState(false);
  
  const nameInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  
  // Reference to the SpeechRecognition instance
  const recognitionRef = useRef<any>(null);
  
  // Check if the browser supports the Web Speech API
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setIsSpeechSupported(!!SpeechRecognition);
    
    // Focus on name input if using fallback UI
    if (!SpeechRecognition && nameInputRef.current) {
      nameInputRef.current.focus();
      setShowTooltip(true);
      
      // Hide tooltip after 5 seconds
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, []);
  
  // Initialize speech recognition
  const initSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) return false;
    
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = false;
    recognitionRef.current.lang = 'en-US';
    
    recognitionRef.current.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript.trim();
      console.log('Speech recognized:', transcript);
      
      if (currentField === 'name') {
        setName(transcript);
        setCurrentField('phone');
        startListening(); // Start listening again for phone
      } else {
        // Clean up phone number - remove spaces, keep only digits, +, and ()
        const cleanedPhone = transcript.replace(/[^\d+() ]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        setPhone(cleanedPhone);
        setIsListening(false);
      }
    };
    
    recognitionRef.current.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };
    
    recognitionRef.current.onend = () => {
      if (isListening) {
        // If we're still supposed to be listening but recognition ended, restart it
        if (currentField === 'phone') {
          startListening();
        }
      }
    };
    
    return true;
  };
  
  // Start listening for speech
  const startListening = () => {
    if (!recognitionRef.current) {
      if (!initSpeechRecognition()) return;
    }
    
    try {
      recognitionRef.current.start();
      setIsListening(true);
    } catch (error) {
      console.error('Error starting speech recognition:', error);
    }
  };
  
  // Stop listening for speech
  const stopListening = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error('Error stopping speech recognition:', error);
      }
    }
    setIsListening(false);
  };
  
  // Handle mic button click
  const handleMicButtonClick = () => {
    if (isListening) {
      stopListening();
    } else {
      setCurrentField('name');
      startListening();
    }
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (name && phone) {
      // Create and download vCard
      const contact = {
        name,
        phone
      };
      
      downloadVCard(contact);
      
      // Call onComplete callback if provided
      if (onComplete) {
        onComplete(name, phone);
      }
      
      // Reset form
      setName('');
      setPhone('');
      setCurrentField('name');
      
      // Focus back on name input if using fallback UI
      if (!isSpeechSupported && nameInputRef.current) {
        nameInputRef.current.focus();
      }
    }
  };
  
  // Handle input change for fallback form
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'name' | 'phone') => {
    const { value } = e.target;
    
    if (field === 'name') {
      setName(value);
    } else {
      setPhone(value);
    }
  };
  
  // Handle keydown for fallback form
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: 'name' | 'phone') => {
    // If Enter is pressed, move to next field or submit form
    if (e.key === 'Enter') {
      e.preventDefault();
      
      if (field === 'name' && name.trim() && phoneInputRef.current) {
        phoneInputRef.current.focus();
      } else if (field === 'phone' && phone.trim() && name.trim()) {
        handleSubmit(e as unknown as React.FormEvent);
      }
    }
  };

  return (
    <div className="voice-contact-form">
      {isSpeechSupported ? (
        // Speech recognition supported UI
        <div className="speech-supported">
          <div className="field-status">
            {isListening ? (
              <p>Listening for {currentField === 'name' ? 'name' : 'phone number'}...</p>
            ) : (
              <p>Click the microphone to start</p>
            )}
          </div>
          
          <button 
            type="button"
            onClick={handleMicButtonClick}
            className={`mic-button ${isListening ? 'listening' : ''}`}
            aria-label={isListening ? 'Stop recording' : 'Start recording'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          </button>
          
          {(name || phone) && (
            <div className="captured-data">
              {name && <p><strong>Name:</strong> {name}</p>}
              {phone && <p><strong>Phone:</strong> {phone}</p>}
              
              {name && phone && (
                <button 
                  type="button"
                  onClick={(e) => handleSubmit(e)}
                  className="next-button"
                >
                  Create Contact
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        // Fallback UI for browsers without speech recognition
        <form onSubmit={handleSubmit} className="fallback-form">
          <div className="form-field">
            <label htmlFor="name">Name</label>
            <div className="input-wrapper">
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => handleInputChange(e, 'name')}
                onKeyDown={(e) => handleKeyDown(e, 'name')}
                placeholder="Enter your name"
                ref={nameInputRef}
                autoFocus
                required
              />
              {showTooltip && (
                <div className="tooltip">
                  Tap the mic icon on your keyboard and speak your name
                </div>
              )}
            </div>
          </div>
          
          <div className="form-field">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => handleInputChange(e, 'phone')}
              onKeyDown={(e) => handleKeyDown(e, 'phone')}
              placeholder="Enter your phone number"
              ref={phoneInputRef}
              required
            />
          </div>
          
          {name && phone && (
            <button 
              type="submit"
              className="next-button"
            >
              Next
            </button>
          )}
        </form>
      )}
    </div>
  );
};

// Add type definitions for the Web Speech API
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}
